import React from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { Power as PowerIcon, PowerSettingsNew as PowerOffIcon } from '@mui/icons-material';
import { useConfirmDialog } from 'new/elements/ConfirmDialog';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { showAlert } from 'new/stores/showAlert/action';
import { useActivateAdminOrg, useDeactivateAdminOrg } from 'new/stores/adminOrgs/adminOrgs.api';
import { AdminOrg } from 'new/stores/adminOrgs/adminOrgs.types';
import { AdminRole } from '../../RoleBanner';
import { GuardedControl } from '../../GuardedControl';
import tabStyles from './tabs.module.scss';

interface DangerZoneTabProps {
  org: AdminOrg;
  role: AdminRole;
}

export function DangerZoneTab({ org, role }: DangerZoneTabProps) {
  const dispatch = useAppDispatch();
  const [activateOrg, { isLoading: isActivating }] = useActivateAdminOrg();
  const [deactivateOrg, { isLoading: isDeactivating }] = useDeactivateAdminOrg();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const disabled = role !== 'full';
  const isDeactivated = org.status === 'DEACTIVATED';

  const onSuspend = () => {
    confirm({
      title: 'Suspend org',
      message: (
        <>
          This blocks all users in &quot;{org.name}&quot; from logging into the org. Data is
          retained and this is reversible.
          <br />
          This does not freeze flows. Use the <i>nexla-ops-ts</i> CLI to freeze flows.
        </>
      ),
      confirmText: 'Suspend',
      variant: 'danger',
      onConfirm: () =>
        deactivateOrg(org.id).then((result) => {
          if ('error' in result) {
            dispatch(showAlert({ title: `Failed to suspend "${org.name}"`, severity: 'error' }));
          }
        }),
    });
  };

  const onReactivate = () => {
    confirm({
      title: 'Reactivate org',
      message: `This restores user access for "${org.name}".`,
      confirmText: 'Reactivate',
      variant: 'info',
      onConfirm: () =>
        activateOrg(org.id).then((result) => {
          if ('error' in result) {
            dispatch(showAlert({ title: `Failed to reactivate "${org.name}"`, severity: 'error' }));
          }
        }),
    });
  };

  return (
    <Stack spacing={2}>
      {!isDeactivated && (
        <div className={tabStyles.dangerCardDestructive}>
          <Stack direction="row" spacing={2} alignItems="center">
            <PowerOffIcon color="error" className={tabStyles.dangerIcon} />
            <div>
              <Typography variant="subtitle1">Suspend org</Typography>
              <Typography variant="body2" color="text.secondary">
                Blocks all user access. Data is retained. Reversible.
                <br />
                This does not freeze flows. Use the <i>nexla-ops-ts</i> CLI to freeze flows.
              </Typography>
            </div>
          </Stack>
          <GuardedControl disabled={disabled || isDeactivating}>
            <Button variant="contained" color="error" onClick={onSuspend}>
              Suspend
            </Button>
          </GuardedControl>
        </div>
      )}

      {isDeactivated && (
        <div className={tabStyles.dangerCardSuccess}>
          <Stack direction="row" spacing={2} alignItems="center">
            <PowerIcon color="success" className={tabStyles.dangerIcon} />
            <div>
              <Typography variant="subtitle1">Reactivate org</Typography>
              <Typography variant="body2" color="text.secondary">
                Restores user access.
              </Typography>
            </div>
          </Stack>
          <GuardedControl disabled={disabled || isActivating}>
            <Button variant="contained" color="success" onClick={onReactivate}>
              Reactivate
            </Button>
          </GuardedControl>
        </div>
      )}

      {ConfirmDialogComponent}
    </Stack>
  );
}
